"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from "./button"
import { cn } from "./lib/utils"

interface DateTimePickerDialogProps {
  /**
   * ISO datetime string, or a `Date` object accepted for parent-portal
   * ports that bind it directly. The dialog normalises both to its
   * internal parts via `new Date(...)`.
   */
  value?: string | Date
  /**
   * Change callback. The dialog emits ISO strings; the parameter is
   * intentionally loose so parent-portal callers that bind a Date-
   * accepting handler (they call `.toISOString()` themselves) still
   * type-check without changing the internal emission shape.
   */
  onChange?: (datetime: any) => void
  onClose?: () => void
  onDraggingChange?: (isDragging: boolean) => void
}

type ViewMode = "date" | "month" | "year"

export function DateTimePickerDialog({ value, onChange, onClose, onDraggingChange }: DateTimePickerDialogProps) {
  // Parse initial value or use current date/time
  const parseDateTime = (datetimeString?: string | Date) => {
    if (datetimeString) {
      const date = new Date(datetimeString as any)
      return {
        date: date,
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds(),
      }
    }
    const now = new Date()
    return {
      date: now,
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    }
  }

  const initialDateTime = parseDateTime(value)
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(value ? initialDateTime.date : null)
  const [hour, setHour] = React.useState(initialDateTime.hour)
  const [minute, setMinute] = React.useState(initialDateTime.minute)
  const [second, setSecond] = React.useState(initialDateTime.second)
  const [hasTimeChanged, setHasTimeChanged] = React.useState(false)
  const [viewMode, setViewMode] = React.useState<ViewMode>("date")
  const [isDragging, setIsDragging] = React.useState<"hour" | "minute" | "second" | null>(null)

  const today = new Date()
  const [displayMonth, setDisplayMonth] = React.useState(
    selectedDate?.getMonth() ?? initialDateTime.date.getMonth() ?? today.getMonth(),
  )
  const [displayYear, setDisplayYear] = React.useState(
    selectedDate?.getFullYear() ?? initialDateTime.date.getFullYear() ?? today.getFullYear(),
  )

  const hourRef = React.useRef<HTMLDivElement>(null)
  const minuteRef = React.useRef<HTMLDivElement>(null)
  const secondRef = React.useRef<HTMLDivElement>(null)

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const monthNamesFull = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]
  const dayNames = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"]

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay()
  }

  const formatTime = (h: number, m: number, s: number) => {
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  }

  const formatDateTime = (date: Date, h: number, m: number, s: number) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    const hours = String(h).padStart(2, "0")
    const minutes = String(m).padStart(2, "0")
    const seconds = String(s).padStart(2, "0")
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }

  const handleDateClick = (day: number) => {
    const newDate = new Date(displayYear, displayMonth, day)
    setSelectedDate(newDate)
  }

  const handleMonthClick = (monthIndex: number) => {
    setDisplayMonth(monthIndex)
    setViewMode("date")
  }

  const handleYearClick = (year: number) => {
    setDisplayYear(year)
    setViewMode("month")
  }

  const handleNow = () => {
    const now = new Date()
    setSelectedDate(now)
    setHour(now.getHours())
    setMinute(now.getMinutes())
    setSecond(now.getSeconds())
    setDisplayMonth(now.getMonth())
    setDisplayYear(now.getFullYear())
    onChange?.(formatDateTime(now, now.getHours(), now.getMinutes(), now.getSeconds()))
    onClose?.()
  }

  const handleSave = () => {
    // If date is set, use it; otherwise use current date
    const dateToUse = selectedDate || new Date()
    onChange?.(formatDateTime(dateToUse, hour, minute, second))
    onClose?.()
  }

  const handlePrevious = () => {
    if (viewMode === "date") {
      if (displayMonth === 0) {
        setDisplayMonth(11)
        setDisplayYear(displayYear - 1)
      } else {
        setDisplayMonth(displayMonth - 1)
      }
    } else if (viewMode === "month") {
      setDisplayYear(displayYear - 1)
    } else if (viewMode === "year") {
      setDisplayYear(displayYear - 10)
    }
  }

  const handleNext = () => {
    if (viewMode === "date") {
      if (displayMonth === 11) {
        setDisplayMonth(0)
        setDisplayYear(displayYear + 1)
      } else {
        setDisplayMonth(displayMonth + 1)
      }
    } else if (viewMode === "month") {
      setDisplayYear(displayYear + 1)
    } else if (viewMode === "year") {
      setDisplayYear(displayYear + 10)
    }
  }

  const calculateValue = (clientX: number, ref: React.RefObject<HTMLDivElement>, max: number) => {
    if (!ref.current) return 0
    const rect = ref.current.getBoundingClientRect()
    const x = clientX - rect.left
    const percentage = Math.max(0, Math.min(1, x / rect.width))
    return Math.round(percentage * max)
  }

  const handleMouseDown = (type: "hour" | "minute" | "second") => (e: React.MouseEvent) => {
    setIsDragging(type)
    setHasTimeChanged(true)
    onDraggingChange?.(true)
    handleMouseMove(e, type)
  }

  const handleMouseMove = (e: React.MouseEvent | MouseEvent, type?: "hour" | "minute" | "second") => {
    const dragType = type || isDragging
    if (!dragType) return

    if (dragType === "hour") {
      const newHour = calculateValue(e.clientX, hourRef, 23)
      setHour(newHour)
    } else if (dragType === "minute") {
      const newMinute = calculateValue(e.clientX, minuteRef, 59)
      setMinute(newMinute)
    } else if (dragType === "second") {
      const newSecond = calculateValue(e.clientX, secondRef, 59)
      setSecond(newSecond)
    }
  }

  React.useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e)
      const handleGlobalMouseUp = () => {
        setIsDragging(null)
        onDraggingChange?.(false)
      }

      document.addEventListener("mousemove", handleGlobalMouseMove)
      document.addEventListener("mouseup", handleGlobalMouseUp)

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove)
        document.removeEventListener("mouseup", handleGlobalMouseUp)
      }
    }
  }, [isDragging])

  const renderDateView = () => {
    const daysInMonth = getDaysInMonth(displayMonth, displayYear)
    const firstDay = getFirstDayOfMonth(displayMonth, displayYear)
    const prevMonthDays =
      displayMonth === 0 ? getDaysInMonth(11, displayYear - 1) : getDaysInMonth(displayMonth - 1, displayYear)

    const days: React.ReactElement[] = []

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(
        <button
          key={`prev-${i}`}
          className="h-9 w-9 text-center text-sm text-light-text-secondary dark:text-dark-text-secondary opacity-40"
          disabled
        >
          {prevMonthDays - i}
        </button>,
      )
    }

    // Current month days
    const todayDate = new Date()
    const isCurrentMonth = displayMonth === todayDate.getMonth() && displayYear === todayDate.getFullYear()

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = isCurrentMonth && day === todayDate.getDate()
      const isSelected =
        selectedDate &&
        day === selectedDate.getDate() &&
        displayMonth === selectedDate.getMonth() &&
        displayYear === selectedDate.getFullYear()

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={cn(
            "h-9 w-9 text-center text-sm rounded-md transition-colors",
            "hover:bg-primary/10 hover:text-primary",
            "text-light-text-primary dark:text-dark-text-primary",
            isToday && "font-semibold text-primary",
            isSelected && "bg-primary text-white hover:bg-primary hover:text-white",
          )}
        >
          {day}
        </button>,
      )
    }

    // Next month days
    const remainingDays = 42 - days.length
    for (let i = 1; i <= remainingDays; i++) {
      days.push(
        <button
          key={`next-${i}`}
          className="h-9 w-9 text-center text-sm text-light-text-secondary dark:text-dark-text-secondary opacity-40"
          disabled
        >
          {i}
        </button>,
      )
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-7 gap-1">
          {dayNames.map((day) => (
            <div
              key={day}
              className="h-9 w-9 text-center text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary flex items-center justify-center"
            >
              {day}
            </div>
          ))}
          {days}
        </div>
      </div>
    )
  }

  const renderMonthView = () => {
    const todayDate = new Date()
    const isCurrentYear = displayYear === todayDate.getFullYear()

    return (
      <div className="grid grid-cols-4 gap-4 py-4">
        {monthNames.map((month, index) => {
          const isCurrentMonth = isCurrentYear && index === todayDate.getMonth()
          const isSelected =
            selectedDate && index === selectedDate.getMonth() && displayYear === selectedDate.getFullYear()

          return (
            <button
              key={month}
              onClick={() => handleMonthClick(index)}
              className={cn(
                "h-12 rounded-md text-sm font-medium transition-colors",
                "hover:bg-primary/10 hover:text-primary",
                "text-light-text-primary dark:text-dark-text-primary",
                isCurrentMonth && "text-primary font-semibold",
                isSelected && "bg-primary text-white hover:bg-primary hover:text-white",
              )}
            >
              {month}
            </button>
          )
        })}
      </div>
    )
  }

  const renderYearView = () => {
    const startYear = Math.floor(displayYear / 10) * 10
    const years: React.ReactElement[] = []
    const todayDate = new Date()
    const currentYear = todayDate.getFullYear()

    for (let i = startYear; i < startYear + 10; i++) {
      const isCurrentYear = i === currentYear
      const isSelected = selectedDate && i === selectedDate.getFullYear()

      years.push(
        <button
          key={i}
          onClick={() => handleYearClick(i)}
          className={cn(
            "h-12 rounded-md text-sm font-medium transition-colors",
            "hover:bg-primary/10 hover:text-primary",
            "text-light-text-primary dark:text-dark-text-primary",
            isCurrentYear && "text-primary font-semibold",
            isSelected && "bg-primary text-white hover:bg-primary hover:text-white",
          )}
        >
          {i}
        </button>,
      )
    }

    return <div className="grid grid-cols-4 gap-4 py-4">{years}</div>
  }

  const getHeaderText = () => {
    if (viewMode === "date") {
      return `${monthNamesFull[displayMonth]}, ${displayYear}`
    } else if (viewMode === "month") {
      return displayYear.toString()
    } else {
      const startYear = Math.floor(displayYear / 10) * 10
      return `${startYear} - ${startYear + 9}`
    }
  }

  const SliderRow = ({
    label,
    value,
    max,
    sliderRef,
    onMouseDown,
  }: {
    label: string
    value: number
    max: number
    sliderRef: React.RefObject<HTMLDivElement>
    onMouseDown: (e: React.MouseEvent) => void
  }) => {
    const percentage = (value / max) * 100

    return (
      <div className="flex items-center gap-3">
        <div className="w-5 text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">{label}</div>
        <div className="flex-1 relative min-w-0" ref={sliderRef}>
          <div
            className="h-1 rounded-full cursor-pointer"
            onMouseDown={onMouseDown}
            style={{
              background: `linear-gradient(to right, rgb(124, 58, 237) 0%, rgb(124, 58, 237) ${percentage}%, rgb(209, 213, 219) ${percentage}%, rgb(209, 213, 219) 100%)`,
            }}
          >
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full cursor-grab active:cursor-grabbing shadow-md"
              style={{ left: `${percentage}%`, transform: "translate(-50%, -50%)" }}
              onMouseDown={onMouseDown}
            />
          </div>
        </div>
        <div className="w-6 text-sm font-medium text-light-text-primary dark:text-dark-text-primary text-right">
          {value}
        </div>
      </div>
    )
  }

  return (
    <div className="w-auto p-4 bg-light-card-bg dark:bg-dark-card-bg border border-gray-200 dark:border-gray-700/16 rounded-lg shadow-lg">
      <div className="space-y-4">
        {/* Date Picker Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-light-table-header dark:hover:bg-dark-table-header transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-light-text-primary dark:text-dark-text-primary" />
          </button>

          <button
            onClick={() => {
              if (viewMode === "date") {
                setViewMode("month")
              } else if (viewMode === "month") {
                setViewMode("year")
              }
            }}
            className={cn(
              "text-sm font-medium px-3 py-1 rounded-md transition-colors",
              "text-light-text-primary dark:text-dark-text-primary",
              viewMode !== "year" && "hover:bg-primary/10 hover:text-primary",
            )}
          >
            {getHeaderText()}
          </button>

          <button
            onClick={handleNext}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-light-table-header dark:hover:bg-dark-table-header transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-light-text-primary dark:text-dark-text-primary" />
          </button>
        </div>

        {/* Date Picker Content */}
        {viewMode === "date" && renderDateView()}
        {viewMode === "month" && renderMonthView()}
        {viewMode === "year" && renderYearView()}

        {/* Divider */}
        <div className="border-t border-gray-200 dark:border-gray-700/16" />

        {/* Time Display */}
        <div className="text-center">
          <div className="text-3xl font-bold text-primary">{formatTime(hour, minute, second)}</div>
        </div>

        {/* Time Sliders */}
        <div className="space-y-3 py-2">
          <SliderRow label="H" value={hour} max={23} sliderRef={hourRef} onMouseDown={handleMouseDown("hour")} />
          <SliderRow label="M" value={minute} max={59} sliderRef={minuteRef} onMouseDown={handleMouseDown("minute")} />
          <SliderRow label="S" value={second} max={59} sliderRef={secondRef} onMouseDown={handleMouseDown("second")} />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 pt-2 border-t border-gray-200 dark:border-gray-700/16">
          <Button onClick={handleNow} variant="ghost" size="sm" className="text-primary hover:text-primary/90">
            Now
          </Button>
          <Button
            onClick={handleSave}
            variant="default"
            size="sm"
            className="bg-primary hover:bg-primary/90 text-white"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
